/**
 * SubscriptionPlans component for displaying and selecting subscription plans.
 * 
 * This component fetches available subscription plans from the API and displays them
 * in a card-based layout. Users can select a plan to initiate the checkout process.
 * 
 * Key Features:
 * - Fetches and displays available subscription plans
 * - Shows current subscription status
 * - Initiates Stripe checkout for plan selection
 * - Handles loading and error states
 * 
 * File Dependencies:
 * This file uses: React, Material-UI, utils/api, @stripe/stripe-js
 * This file is used by: Pages that need subscription plan selection
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { 
  fetchSubscriptionPlans, 
  fetchSubscriptionStatus, 
  createCheckoutSession 
} from '../utils/api';
import FoundersDealBanner from './FoundersDealBanner';
import analytics from '../services/analytics';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both plans and current status
      const [plansResponse, statusResponse] = await Promise.all([
        fetchSubscriptionPlans(),
        fetchSubscriptionStatus()
      ]);
      
      setPlans(plansResponse.data);
      setCurrentStatus(statusResponse.data);
      
      // Track that user viewed subscription plans
      if (plansResponse.data.length > 0) {
        plansResponse.data.forEach(plan => {
          analytics.trackSubscriptionPlanViewed(plan.plan_id, plan.plan_name, {
            price_cents: plan.price_cents,
            exam_allowance: plan.exam_allowance,
            currency: plan.currency,
            has_active_subscription: statusResponse.data?.has_active_subscription || false,
            is_alpha_tester: statusResponse.data?.message === "Alpha tester with extended trial privileges"
          });
        });
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    try {
      setProcessingPlanId(planId);
      setError(null);
      
      // Find the selected plan details
      const selectedPlan = plans.find(p => p.plan_id === planId);
      
      // Track checkout started
      analytics.trackSubscriptionCheckoutStarted(
        planId, 
        selectedPlan?.plan_name || 'Unknown',
        selectedPlan?.price_cents || 0,
        {
          exam_allowance: selectedPlan?.exam_allowance,
          is_alpha_tester: currentStatus?.message === "Alpha tester with extended trial privileges",
          has_active_subscription: currentStatus?.has_active_subscription || false
        }
      );
      
      // Create checkout session (backend handles founder's deal automatically for alpha testers)
      const response = await createCheckoutSession(
        planId,
        `${window.location.origin}/subscription/success`,
        `${window.location.origin}/subscription/plans`
      );
      
      // Redirect to Stripe checkout
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({
        sessionId: response.data.session_id
      });
      
      if (error) {
        console.error('Stripe redirect error:', error);
        setError(error.message);
        
        // Track checkout abandoned due to error
        analytics.trackSubscriptionCheckoutAbandoned(
          planId,
          selectedPlan?.plan_name || 'Unknown',
          {
            error_message: error.message,
            error_type: 'stripe_redirect_error'
          }
        );
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout process');
      
      // Track checkout abandoned due to API error
      const selectedPlan = plans.find(p => p.plan_id === planId);
      analytics.trackSubscriptionCheckoutAbandoned(
        planId,
        selectedPlan?.plan_name || 'Unknown',
        {
          error_message: err.response?.data?.detail || err.message,
          error_type: 'api_error'
        }
      );
    } finally {
      setProcessingPlanId(null);
    }
  };

  const formatPrice = (priceCents, currency) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    });
    return formatter.format(priceCents / 100);
  };

  const formatAllowance = (allowance) => {
    if (allowance === null) return 'Unlimited';
    return `${allowance} per month`;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Choose Your Subscription Plan
        </Typography>
        
        {/* Founder's Deal Banner */}
        <FoundersDealBanner showOnlyIfAvailable={true} />
        
        {/* Current Status Alert */}
        {currentStatus && (
          <Box sx={{ mb: 4 }}>
            {currentStatus.has_active_subscription ? (
              <Alert severity="info">
                You have an active subscription. Current plan allows {formatAllowance(currentStatus.exam_allowance)} exams.
                You've used {currentStatus.allowance_used} exam{currentStatus.allowance_used !== 1 ? 's' : ''} this period.
              </Alert>
            ) : currentStatus.message === "Alpha tester with extended trial privileges" ? (
              <Alert severity="success">
                Welcome Founding Member! You have {currentStatus.allowance_remaining} of your {currentStatus.exam_allowance} trial exams remaining.
                Your Founder's Deal discount will be automatically applied at checkout!
              </Alert>
            ) : currentStatus.message === "Trial user with limited exams" ? (
              <Alert severity="warning">
                You're in your free trial period. You have used {currentStatus.allowance_used} of your {currentStatus.exam_allowance} free exams.
              </Alert>
            ) : (
              <Alert severity="info">
                Select a plan below to start generating exams.
              </Alert>
            )}
          </Box>
        )}
        
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.plan_id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  ...(plan.plan_name.toLowerCase().includes('premium') && {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    borderStyle: 'solid'
                  })
                }}
              >
                {plan.plan_name.toLowerCase().includes('premium') && (
                  <Chip 
                    label="Most Popular" 
                    color="primary" 
                    sx={{ 
                      position: 'absolute', 
                      top: -12, 
                      left: '50%', 
                      transform: 'translateX(-50%)' 
                    }}
                  />
                )}
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h2" gutterBottom align="center">
                    {plan.plan_name}
                  </Typography>
                  
                  <Typography variant="h3" component="div" align="center" sx={{ mb: 2 }}>
                    {formatPrice(plan.price_cents, plan.currency)}
                    <Typography variant="body2" color="text.secondary" component="span">
                      /month
                    </Typography>
                  </Typography>
                  
                  <Typography variant="body1" align="center" sx={{ mb: 2 }}>
                    {formatAllowance(plan.exam_allowance)} exams
                  </Typography>
                  
                  {/* Add feature list here if available */}
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant={plan.plan_name.toLowerCase().includes('premium') ? 'contained' : 'outlined'}
                    color="primary"
                    size="large"
                    onClick={() => handleSelectPlan(plan.plan_id)}
                    disabled={
                      processingPlanId !== null || 
                      (currentStatus?.has_active_subscription && !currentStatus?.cancel_at_period_end)
                    }
                  >
                    {processingPlanId === plan.plan_id ? (
                      <CircularProgress size={24} />
                    ) : currentStatus?.has_active_subscription ? (
                      'Current Plan'
                    ) : (
                      'Select Plan'
                    )}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
}

export default SubscriptionPlans;